import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated } from '@/features/auth/store/selectors';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, Edit3, Users, Zap } from 'lucide-react';

export const HomePage = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const handleCreateBlog = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      navigate('/auth/login', { state: { from: '/profile' } });
    }
  };

  return (
    <div className="flex-1">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Tạo Blog Của Riêng Bạn
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Nền tảng blog đơn giản, mạnh mẽ và dễ sử dụng. Chia sẻ câu chuyện của bạn với thế giới.
            </p>
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 h-auto"
              onClick={handleCreateBlog}
            >
              <Edit3 className="mr-2 h-5 w-5" />
              Tạo Blog Ngay
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Tính Năng Nổi Bật
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {/* Feature 1 */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Quản Lý Đa Blog</h3>
                  <p className="text-gray-600">
                    Tạo và quản lý nhiều blog khác nhau. Mỗi blog có thể có chủ đề riêng và đội ngũ quản trị viên.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <Edit3 className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Soạn Thảo Dễ Dàng</h3>
                  <p className="text-gray-600">
                    Trình soạn thảo trực quan với hỗ trợ danh mục, hình ảnh và quản lý trạng thái bài viết.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Cộng Tác Nhóm</h3>
                  <p className="text-gray-600">
                    Thêm quản trị viên để cùng nhau xây dựng và phát triển blog của bạn.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Cách Hoạt Động
            </h2>
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Đăng ký tài khoản</h3>
                  <p className="text-gray-600">
                    Tạo tài khoản miễn phí trong vài giây. Sử dụng email hoặc đăng nhập bằng Google.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Tạo blog của bạn</h3>
                  <p className="text-gray-600">
                    Đặt tên cho blog, thiết kế banner và tùy chỉnh các danh mục theo ý muốn.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Viết và chia sẻ</h3>
                  <p className="text-gray-600">
                    Bắt đầu viết bài, thêm hình ảnh và xuất bản nội dung của bạn ra thế giới.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-white">
            <Zap className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              Sẵn sàng bắt đầu chưa?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Tham gia cùng hàng ngàn blogger đang chia sẻ câu chuyện của họ
            </p>
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 h-auto"
              onClick={handleCreateBlog}
            >
              Bắt Đầu Miễn Phí
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};